import React from 'react';
import { Helmet } from 'react-helmet'; // SEO Added
import { Palette, Sofa, PenTool, Layers, Sun, Image as ImageIcon, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import img1 from "../../assets/design1.jpg";
import img2 from "../../assets/design2.jpg";
import img3 from "../../assets/design3.jpg";
import img4 from "../../assets/design4.jpg";
import img5 from "../../assets/design5.jpg";

const designs = [
  { 
    id: 1, 
    title: 'Concept Mood Boards', 
    category: 'Ideation', 
    image: img1,
    desc: 'Visualizing color palettes, textures, and themes before work begins.' 
  },
  { 
    id: 2, 
    title: 'Material Selection', 
    category: 'Sourcing', 
    image: img2,
    desc: 'Handpicking Italian marble, premium veneers, and designer fabrics.'
  },
  { 
    id: 3, 
    title: '3D Visualization', 
    category: 'VR Walkthrough', 
    image: img3,
    desc: 'See your future home in 4K resolution before execution.' 
  },
  { 
    id: 4, 
    title: 'Art & Artifacts', 
    category: 'Styling', 
    image: img4,
    desc: 'Curating paintings, sculptures, and rugs for the final touch.' 
  },
  { 
    id: 5, 
    title: 'False Ceiling Design', 
    category: 'POP Work', 
    image: "https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&q=80",
    desc: 'Modern gypsum ceilings with cove lighting and profile LEDs.' 
  },
  { 
    id: 6, 
    title: 'Wall Treatments', 
    category: 'Finishes', 
    image: "https://images.unsplash.com/photo-1615529182904-14819c35db37?auto=format&fit=crop&q=80",
    desc: 'Luxury wallpapers, texture paint, and fluted wooden paneling.' 
  },
];

const InteriorDesigning = () => {
  // SEO Schema
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Interior Design",
    "provider": {
      "@type": "Organization",
      "name": "Luxuriya Homes"
    },
    "areaServed": ["Noida", "Delhi", "Lucknow", "Gurgaon"],
    "description": "Luxury interior design, turnkey projects, and home styling."
  };

  return (
    <div className="font-sans text-gray-800 bg-white">
      
      {/* SEO HEAD */}
      <Helmet>
        <title>Luxury Interior Designers in Noida, Delhi & Lucknow | Turnkey Interiors</title>
        <meta name="description" content="Top-rated interior designers in Delhi NCR & UP. We specialize in luxury home interiors, 3D visualization, false ceiling, and bespoke furniture design." />
        <meta name="keywords" content="interior designers in noida, luxury interiors delhi, home decor lucknow, turnkey interior contractors, bedroom design ideas" />
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>

      {/* 1. HERO SECTION */}
      <div
        className="relative h-[75vh] w-full bg-cover bg-center"
        style={{ backgroundImage: `url(${img5})` }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4 animate-fadeIn">
          <h4 className="text-amber-500 font-bold tracking-[0.3em] uppercase mb-3">
             The Soul of the Home
          </h4>
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mb-6">
             Interior Designing
          </h1>
          <p className="text-gray-200 text-lg md:text-xl max-w-2xl font-light leading-relaxed">
             Curating spaces in <strong>Delhi, Noida & Lucknow</strong> that reflect your personality and prestige.
          </p>
        </div>
      </div>

      {/* 2. INTRO SECTION */}
      <section className="py-24 px-6 bg-white text-center">
        <h2 className="text-3xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
           From Concept to Reality
        </h2>
        <div className="w-24 h-1 bg-amber-500 mx-auto mb-8"></div>
        <p className="text-gray-600 max-w-4xl mx-auto leading-loose text-lg">
          Our interior design service goes beyond decoration. It is a science of measurement and an art of aesthetics. 
          We analyze your lifestyle, lighting, and spatial flow to create bespoke interiors. 
          From selecting the perfect Velvet for your sofa in Noida to placing the final Brass artifact in Varanasi, we handle it all with precision.
        </p>
      </section>

      {/* 3. EXPANDED GALLERY */}
      <section className="py-20 bg-neutral-50 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
             <h3 className="text-4xl font-serif font-bold text-gray-900 mb-2">Design Process</h3>
             <p className="text-gray-500">Every step detailed for perfection</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {designs.map((item) => (
              <div key={item.id} className="group relative overflow-hidden shadow-lg cursor-pointer rounded-sm bg-white hover:shadow-2xl transition-all duration-300">
                <div className="h-72 overflow-hidden">
                  <img 
                    src={item.image} 
                    alt={`${item.title} - Interior Design Step`} 
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-110 transition duration-700"
                  />
                </div>
                <div className="p-8 border-b-4 border-transparent group-hover:border-amber-500 transition-all">
                  <p className="text-amber-600 text-xs tracking-widest uppercase mb-2 font-bold">{item.category}</p>
                  <h4 className="text-xl font-serif text-gray-900 mb-2">{item.title}</h4>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. DESIGN ELEMENTS (ICONS) */}
      <section className="py-24 px-6 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto">
           <div className="text-center mb-16">
              <h3 className="text-3xl md:text-4xl font-serif font-bold mb-4">The Luxuriya Touch</h3>
              <p className="text-gray-400">We focus on the 3 pillars of design: Comfort, Style, and Functionality.</p>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
              <div className="flex flex-col items-center">
                <Palette className="w-12 h-12 text-amber-500 mb-6" />
                <h4 className="text-xl font-bold mb-3">Color Consultancy</h4>
                <p className="text-gray-400 leading-relaxed">
                   Understanding the psychology of color. We choose palettes that evoke calmness or energy based on the room's purpose.
                </p>
              </div>
              <div className="flex flex-col items-center">
                <Sofa className="w-12 h-12 text-amber-500 mb-6" />
                <h4 className="text-xl font-bold mb-3">Furniture Layout</h4>
                <p className="text-gray-400 leading-relaxed">
                   Optimizing flow. Ensuring you can walk through your room comfortably while maximizing seating and utility.
                </p>
              </div>
              <div className="flex flex-col items-center">
                <Sun className="w-12 h-12 text-amber-500 mb-6" />
                <h4 className="text-xl font-bold mb-3">Lighting Design</h4>
                <p className="text-gray-400 leading-relaxed">
                   Layering Ambient, Task, and Accent lighting to create depth, drama, and warmth in the evening.
                </p>
              </div>
              
              {/* Row 2 */}
              <div className="flex flex-col items-center mt-8">
                <Layers className="w-12 h-12 text-amber-500 mb-6" />
                <h4 className="text-xl font-bold mb-3">Texture Balancing</h4>
                <p className="text-gray-400 leading-relaxed">Mixing leather, velvet, metal, and wood to create a rich, tactile experience that feels luxurious.</p>
              </div>
              <div className="flex flex-col items-center mt-8">
                <PenTool className="w-12 h-12 text-amber-500 mb-6" />
                <h4 className="text-xl font-bold mb-3">Custom Joinery</h4>
                <p className="text-gray-400 leading-relaxed">Designing built-in units, TV consoles, and vanity counters that fit perfectly into your space.</p>
              </div>
              <div className="flex flex-col items-center mt-8">
                <ImageIcon className="w-12 h-12 text-amber-500 mb-6" />
                <h4 className="text-xl font-bold mb-3">Styling & Decor</h4>
                <p className="text-gray-400 leading-relaxed">The final layer. Rugs, cushions, vases, and curtains that tie the whole look together.</p>
              </div>
           </div>
        </div>
      </section>

      {/* 5. STYLE GUIDE SECTION (NEW) */}
      <section className="py-20 px-6 bg-white">
         <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-12 items-center">
            <div className="w-full md:w-1/2">
               <h3 className="text-3xl font-serif font-bold text-gray-900 mb-6">Choose Your Style</h3>
               <p className="text-gray-600 mb-8">We specialize in various design languages suited for Indian homes.</p>
               <div className="space-y-4">
                  {[
                    "Modern: Clean lines, neutral colors, metal accents.",
                    "Classic: Ornamental moldings, wooden furniture, warm tones.",
                    "Contemporary: Bold art, current trends, unique shapes.",
                    "Fusion: A blend of Indian heritage and modern functionality."
                  ].map((style, idx) => (
                    <div key={idx} className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                       <div className="w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center text-xs font-bold shrink-0">{idx + 1}</div>
                       <p className="text-gray-700 font-medium">{style}</p>
                    </div>
                  ))}
               </div>
            </div>
            <div className="w-full md:w-1/2 h-[400px] rounded-lg overflow-hidden shadow-2xl relative">
                <img src="https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&q=80" className="w-full h-full object-cover" alt="Interior Styles"/>
                <div className="absolute bottom-6 left-6 bg-white px-6 py-3 font-bold text-gray-900 rounded-sm">Explore Designs</div>
            </div>
         </div>
      </section>

      {/* 6. CTA */}
      <section className="py-24 px-6 text-center bg-white border-t border-gray-100">
        <h2 className="text-3xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
           Transform Your Interiors
        </h2>
        <p className="text-gray-600 mb-10 text-lg max-w-2xl mx-auto">
           Book a 60-minute design consultation with our senior designers.
        </p>
        <Link to="/contact" className="inline-block bg-amber-500 hover:bg-amber-600 text-white text-lg font-bold px-10 py-4 rounded-sm transition shadow-lg uppercase tracking-widest">
           Book Design Consultation
        </Link>
      </section>
    </div>
  );
};

export default InteriorDesigning;